import { QueryClient } from "react-query";

/**
 * How many extra attempts a failed request gets before the screen shows an
 * error. Only transient failures are worth retrying: a 4xx is a decision by the
 * API (429 rate limited, 401 expired session, 404 not found) and sending it
 * again just adds load.
 *
 * This matters for the rate limiter: a rate limited client that retries keeps
 * spending the same window, so one 429 used to turn into four requests and made
 * the window refill slower instead of recovering.
 */
const MAX_RETRIES = 2;

export const shouldRetry = (failureCount: number, error: any): boolean => {
  const status = error?.response?.status;

  if (typeof status === "number" && status >= 400 && status < 500) {
    return false;
  }

  return failureCount < MAX_RETRIES;
};

export const buildQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // Several queries mount per screen; refetching all of them on every
        // mount and every time the window regains focus is what pushed a single
        // user past the API rate limit.
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
      },
    },
  });

export const queryClient = buildQueryClient();
